import React from "react";
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { http } from "../axios/axios";
import ProductCard from "../component/ProductCard";
import DynamicBanner from "../component/DynamicBanner";
import { GetImageUrl } from "../helpers/GetImageUrl";
import { getSubCategoryParentId } from "../helpers/getSubCategoryParentId";
import Pagination from "../component/Pagination";
import { useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

const ProductFilter = ({
  filters,
  selectedFilters,
  setSelectedFilters,
  search,
  setSearch,
  isTrendingFromURL,
  type = "catalog",
}) => {
  const [brandOpen, setBrandOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [subCategoryOpen, setSubCategoryOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isIpNetwork = type === "ip-network";
  const ipNetworkBasePath = location.pathname.startsWith("/it-products")
    ? "/it-products"
    : "/it-products";
  const visibleSubCategories = selectedFilters.category
    ? filters.subCategories.filter(
        (subCategory) =>
          String(getSubCategoryParentId(subCategory)) ===
          String(selectedFilters.category),
      )
    : filters.subCategories;

  return (
    <aside
      className="
      bg-white
      border border-slate-200
      rounded
      p-4 sm:p-5
      w-full
      lg:w-72
      h-fit
      shadow-sm
      "
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <h2 className="text-base sm:text-lg text-slate-600 font-semibold">
          Filter Products
        </h2>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setSelectedFilters({
                brand: "",
                category: "",
                subCategory: "",
                trending: false,
              });

              setSearch("");

              navigate(isIpNetwork ? ipNetworkBasePath : "/security-product");
            }}
            className="
        text-xs
        sm:text-sm
        text-red-600
        hover:text-red-700
        font-medium
      "
          >
            Clear
          </button>

          <button
            className="lg:hidden text-sm text-blue-600"
            onClick={() => setFilterOpen(!filterOpen)}
          >
            {filterOpen ? "Hide" : "Show"}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={filterOpen ? "block" : "hidden lg:block"}>
        <input
          type="text"
          placeholder=" Product Name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="
          w-full
          border-2
          border-slate-200
          rounded-lg
          px-3
          py-2
          text-sm
          outline-none
          "
        />

        <div className="mt-4 space-y-5">
          {/* Extra Filters */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedFilters.trending || isTrendingFromURL}
                onChange={(e) => {
                  const checked = e.target.checked;

                  setSelectedFilters((prev) => ({
                    ...prev,
                    trending: checked,
                    brand: "",
                    category: "",
                    subCategory: "",
                  }));

                  if (checked) {
                    navigate("/security-product?trending=true");
                  } else {
                    navigate("/security-product");
                  }
                }}
                className="accent-blue-600"
              />

              <span className="text-sm sm:text-base font-medium">
                Trending Products
              </span>
            </label>
          </div>

          {/* Brands */}

          <div>
            <button
              onClick={() => setBrandOpen(!brandOpen)}
              className="flex justify-between w-full"
            >
              <span className="font-medium text-sm sm:text-base">Brands</span>

              <ChevronDown
                size={18}
                className={brandOpen ? "rotate-180 transition" : "transition"}
              />
            </button>

            {brandOpen && (
              <div className="mt-3 space-y-1">
                {filters.brands.map((brand, index) => (
                  <label
                    key={brand._id || index}
                    className="
                        flex
                        items-center
                        gap-3
                        cursor-pointer
                        rounded
                        p-1.5
                        hover:bg-slate-50
                        "
                  >
                    <input
                      type="checkbox"
                      checked={selectedFilters.brand === brand._id}
                      onChange={(e) => {
                        const newBrand = e.target.checked ? brand._id : "";

                        setSelectedFilters((prev) => ({
                          ...prev,
                          brand: newBrand,

                          // optional — reset subcategory
                          // because new brand may not have it
                          subCategory: "",
                        }));
                      }}
                      className="accent-blue-600"
                    />

                    <span className="text-xs sm:text-sm">
                      {brand.brandName}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Category */}

          <div>
            <button
              onClick={() => setCategoryOpen(!categoryOpen)}
              className="flex justify-between w-full"
            >
              <span className="font-medium text-sm sm:text-base">
                {isIpNetwork ? "IT Product Categories" : "Category"}
              </span>

              <ChevronDown
                size={18}
                className={
                  categoryOpen ? "rotate-180 transition" : "transition"
                }
              />
            </button>

            {categoryOpen && (
              <div className="mt-3 space-y-1">
                {filters.categories.map((category, index) => (
                  <label
                    key={category._id || index}
                    className="
                      flex
                      items-center
                      gap-3
                      cursor-pointer
                      rounded
                      p-1.5
                      hover:bg-slate-50
                      "
                  >
                    <input
                      type="checkbox"
                      checked={selectedFilters.category === category._id}
                      onChange={(e) => {
                        const newCategory = e.target.checked
                          ? category._id
                          : "";

                        setSelectedFilters((prev) => ({
                          ...prev,
                          category: newCategory,
                          subCategory: "",
                        }));
                      }}
                      className="accent-blue-600"
                    />

                    <span className="text-xs sm:text-sm">
                      {isIpNetwork ? category.name?.toUpperCase() : category.name}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {/* Sub Category */}

          <div>
            <button
              onClick={() => setSubCategoryOpen(!subCategoryOpen)}
              className="flex justify-between w-full"
            >
              <span className="font-medium text-sm sm:text-base">
                {isIpNetwork ? "IT Product Subcategories" : "Sub Categories"}
              </span>

              <ChevronDown
                size={18}
                className={
                  subCategoryOpen ? "rotate-180 transition" : "transition"
                }
              />
            </button>

            {subCategoryOpen && (
              <div className="mt-3 space-y-1 max-h-64 overflow-y-auto">
                {visibleSubCategories?.map((subCategory, index) => (
                  <label
                    key={subCategory._id || index}
                    className="
            flex
            items-center
            gap-3
            cursor-pointer
            rounded
            p-1.5
            hover:bg-slate-50
          "
                  >
                    <input
                      type="checkbox"
                      checked={selectedFilters.subCategory === subCategory._id}
                      onChange={(e) => {
                        const newSubCategory = e.target.checked
                          ? subCategory._id
                          : "";

                        if (isIpNetwork) {
                          setSelectedFilters((prev) => ({
                            ...prev,
                            subCategory: newSubCategory,
                          }));

                          if (newSubCategory) {
                            navigate(
                              `/it-products/sub-category/${subCategory.slug}`,
                            );
                          }
                          return;
                        }

                        setSelectedFilters((prev) => ({
                          ...prev,
                          brand: "",
                          subCategory: newSubCategory,
                        }));

                        navigate(
                          newSubCategory
                            ? `/security-product/sub-category/${subCategory.slug}`
                            : "/security-product",
                        );
                      }}
                      className="accent-blue-600"
                    />

                    <span className="text-xs sm:text-sm">
                      {isIpNetwork
                        ? subCategory.name?.toUpperCase()
                        : subCategory.name}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

const ProductListing = ({ type = "catalog" }) => {
  const { brandSlug, categorySlug, subCategorySlug } = useParams();
  const location = useLocation();
  const isIpNetworkRoute =
    type === "ip-network" || location.pathname.startsWith("/it-products/");
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [urlSyncKey, setUrlSyncKey] = useState(null);
  const [productDefaultSEO, setProductDefaultSEO] = useState({
    title: "Security Products | Suraksha",
    description:
      "Explore premium CCTV cameras, surveillance systems, biometric devices, access control systems and security solutions.",
    keywords:
      "CCTV Cameras, Security Systems, Surveillance Cameras, Biometric Attendance, Access Control",
    schemaScript: null,
  });

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");

  const [searchParams] = useSearchParams();
  const isTrendingFromURL = searchParams.get("trending") === "true";
  const currentUrlKey = `${brandSlug || ""}|${categorySlug || ""}|${subCategorySlug || ""}|${isTrendingFromURL}`;
  const [seoData, setSeoData] = useState(productDefaultSEO);
  const [pageBanners, setPageBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(true);

  const fetchSeo = async () => {
    try {
      const { data } = await http.get("/seo/Product");

      if (data?.data) {
        setProductDefaultSEO({
          title: data.data.metaTitle,
          description: data.data.metaDescription,
          keywords: Array.isArray(data.data.metaTags)
            ? data.data.metaTags.join(", ")
            : data.data.metaTags,
          schemaScript: data.data.schemaScript,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  /* useEffect(() => {
    fetchSeo();
  }, []); */
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    limit: 40,
  });

  const [filters, setFilters] = useState({
    brands: [],
    categories: [],
    subCategories: [],
  });

  const [selectedFilters, setSelectedFilters] = useState({
    brand: "",
    category: "",
    subCategory: "",
    trending: false,
  });

  useEffect(() => {
    if (!filtersLoaded) return;

    const brand = brandSlug
      ? filters.brands.find((b) => b.slug === brandSlug)
      : null;

    const category = categorySlug
      ? filters.categories.find((c) => c.slug === categorySlug)
      : null;

    const subCategory = subCategorySlug
      ? filters.subCategories.find((s) => s.slug === subCategorySlug)
      : null;
    const ipNetworkCategoryId =
      subCategory?.category?._id || subCategory?.category || "";

    setSelectedFilters((prev) => ({
      ...prev,

      // Only update a filter when its URL param exists.
      // Do NOT erase manually selected filters.
      ...(brandSlug && brand ? { brand: brand._id } : {}),

      ...(categorySlug && category ? { category: category._id } : {}),

      ...(subCategorySlug && subCategory
        ? { subCategory: subCategory._id }
        : {}),

      ...(isIpNetworkRoute && subCategory
        ? { category: ipNetworkCategoryId }
        : {}),

      ...(isTrendingFromURL ? { trending: true } : {}),
    }));
  }, [
    filtersLoaded,
    isIpNetworkRoute,
    brandSlug,
    categorySlug,
    subCategorySlug,
    isTrendingFromURL,
    filters,
  ]);

  const handleCatalogFetch = async () => {
    try {
      if (isIpNetworkRoute) {
        const [brandResponse, categoryResponse, subCategoryResponse] =
          await Promise.all([
            http.get("/ip-network/brands", { params: { limit: 100 } }),
          http.get("/ip-network/categories", { params: { limit: 100 } }),
          http.get("/ip-network/subcategories", {
            params: { limit: 1000 },
          }),
          ]);

        setFilters({
          brands: brandResponse.data?.brands || [],
          categories: categoryResponse.data?.categories || [],
          subCategories: subCategoryResponse.data?.subCategories || [],
        });
        setFiltersLoaded(true);
        return;
      }

      const res = await http.get("/products/filter-options");

      setFilters({
        brands: res.data.brands || [],
        categories: res.data.categories || [],
        subCategories: res.data.subCategories || [],
      });
      setFiltersLoaded(true);
    } catch (error) {
      console.error(error);
    }
  };
  const requestId = useRef(0);

  const handleProducts = async (currentFilters) => {
    const id = ++requestId.current;

    try {
      setLoading(true);

      if (isIpNetworkRoute && subCategorySlug) {
        const response = await http.get(
          `/products/ip-network/sub-category/${subCategorySlug}`,
          {
            params: {
              page: pagination.page,
              limit: pagination.limit,
              brand: currentFilters.brand || "",
            },
          },
        );

        if (id !== requestId.current) return;

        setProducts(response.data?.products || []);
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data?.totalPages || 1,
        }));

        const subCategory = response.data?.subCategory;
        if (subCategory) {
          setSeoData({
            title: `Buy ${subCategory.name} Online | Suraksha`,
            description:
              subCategory.description ||
              `Explore ${subCategory.name} IP network products.`,
            keywords: `${subCategory.name}, IP Network, Security Products, Suraksha`,
            schemaScript: null,
          });
        }

        return;
      }

      const res = await http.get("/products/filter", {
        params: isIpNetworkRoute
          ? {
              catalogType: "ip-network",
              ipNetworkBrand: currentFilters.brand || "",
              ipNetworkCategory: currentFilters.category || "",
              ipNetworkSubCategory: currentFilters.subCategory || "",
              trending: currentFilters.trending || "",
              search: debouncedSearch,
              page: pagination.page,
              limit: pagination.limit,
            }
          : {
              catalogType: "catalog",
              brand: currentFilters.brand || "",
              category: currentFilters.category || "",
              subCategory: currentFilters.subCategory || "",
              trending: currentFilters.trending || "",
              search: debouncedSearch,
              page: pagination.page,
              limit: pagination.limit,
            },
      });

      // Ignore stale responses
      if (id !== requestId.current) return;

      setProducts(res.data.products);

      setPagination((prev) => ({
        ...prev,
        totalPages: res.data.totalPages || res.data.pages || 1,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      if (id === requestId.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    handleCatalogFetch();
  }, [isIpNetworkRoute]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [selectedFilters, debouncedSearch]);

  useEffect(() => {
    const pageType = location.pathname.startsWith("/it-products")
      ? "it-products"
      : "products";

    const fetchPageBanners = async () => {
      try {
        setBannersLoading(true);
        setPageBanners([]);
        const res = await http.get(`/banners/page/${pageType}`);
        setPageBanners(res.data?.data || []);
      } catch (error) {
        console.error("Product banner fetch error:", error);
        setPageBanners([]);
      } finally {
        setBannersLoading(false);
      }
    };

    fetchPageBanners();
  }, [location.pathname]);

  useEffect(() => {
    if (!filtersLoaded) return;
    // Wait until URL slugs are resolved into selectedFilters so the first
    // Security request is not sent with an empty subCategory/category/brand.
    if (!isIpNetworkRoute && urlSyncKey !== currentUrlKey) return;

    handleProducts(selectedFilters);
  }, [
    filtersLoaded,
    isIpNetworkRoute,
    urlSyncKey,
    currentUrlKey,
    subCategorySlug,
    selectedFilters.brand,
    selectedFilters.category,
    selectedFilters.subCategory,
    selectedFilters.trending,
    debouncedSearch,
    pagination.page,
  ]);

  useEffect(() => {
    if (isIpNetworkRoute || !filtersLoaded) return;

    const brand = filters.brands.find((b) => b.slug === brandSlug);
    const category = filters.categories.find((c) => c.slug === categorySlug);
    const subCategory = filters.subCategories.find(
      (s) => s.slug === subCategorySlug,
    );

    setSelectedFilters((prev) => ({
      ...prev,
      brand: brand?._id || "",
      // Keep the parent category selected on a sub-category URL so the sidebar stays narrowed.
      category:
        category?._id ||
        (subCategory ? String(getSubCategoryParentId(subCategory)) : ""),
      subCategory: subCategory?._id || "",
      trending: isTrendingFromURL,
    }));
    setUrlSyncKey(currentUrlKey);
  }, [
    isIpNetworkRoute,
    filtersLoaded,
    currentUrlKey,
    brandSlug,
    categorySlug,
    subCategorySlug,
    filters,
    isTrendingFromURL,
  ]);

  useEffect(() => {
    const selectedBrand = filters.brands.find(
      (b) => b._id === selectedFilters.brand,
    );
    const selectedCategory = filters.categories.find(
      (c) => c._id === selectedFilters.category,
    );
    const selectedSubCategory = filters.subCategories.find(
      (s) => s._id === selectedFilters.subCategory,
    );

    // BRAND
    if (selectedBrand) {
      setSeoData({
        title:
          selectedBrand.metaTitle ||
          `${selectedBrand.brandName} Security Products | Suraksha`,
        description:
          selectedBrand.metaDescription ||
          `Buy ${selectedBrand.brandName} security products`,
        keywords:
          selectedBrand.keywords ||
          `${selectedBrand.brandName}, CCTV, Security Products`,
      });
      return;
    }

    // CATEGORY
    if (selectedCategory) {
      setSeoData({
        title:
          selectedCategory.metaTitle ||
          `Buy ${selectedCategory.name} Products Online | Suraksha`,

        description:
          selectedCategory.metaDescription ||
          `Explore ${selectedCategory.name} products including advanced security solutions.`,

        keywords:
          selectedCategory.keywords ||
          selectedCategory.metaTags ||
          `${selectedCategory.name}, CCTV, Security System, Suraksha`,
      });

      return;
    }

    if (selectedSubCategory) {
      setSeoData({
        title:
          selectedSubCategory.metaTitle ||
          `Buy ${selectedSubCategory.name} Online | Suraksha`,

        description:
          selectedSubCategory.metaDescription ||
          `Explore ${selectedSubCategory.name} security products.`,

        keywords:
          selectedSubCategory.keywords ||
          selectedSubCategory.metaTags ||
          `${selectedSubCategory.name}, CCTV, Security Products, Suraksha`,
      });

      return;
    }

    // TRENDING
    if (selectedFilters.trending) {
      setSeoData({
        title: "Trending Security Products",
        description: "Explore trending CCTV and security systems",
        keywords: "Trending CCTV, Security",
      });
      return;
    }

    // DEFAULT
    setSeoData(productDefaultSEO);
  }, [selectedFilters, filters]);

  const selectedBrand = filters.brands.find(
    (b) => b._id === selectedFilters.brand,
  );

  const selectedCategory = filters.categories.find(
    (c) => c._id === selectedFilters.category,
  );

  const selectedSubCategory = filters.subCategories.find(
    (s) => s._id === selectedFilters.subCategory,
  );

  const selectedSchema =
    selectedBrand?.schemaScript ||
    selectedCategory?.schemaScript ||
    selectedSubCategory?.schemaScript ||
    productDefaultSEO?.schemaScript;

  const schemaScript = selectedSchema
    ? typeof selectedSchema === "string"
      ? selectedSchema
      : JSON.stringify(selectedSchema)
    : null;

  const schemaType = selectedBrand?.schemaScript
    ? "brand"
    : selectedCategory?.schemaScript
      ? "category"
      : selectedSubCategory?.schemaScript
        ? "subcategory"
        : "default";

  return (
    <>
      <Helmet>
        <title>{seoData?.title}</title>
        <meta name="description" content={seoData?.description} />
        <meta name="keywords" content={seoData?.keywords} />
        {schemaScript && (
          <script
            type="application/ld+json"
            id={`${schemaType}-schema`}
            dangerouslySetInnerHTML={{
              __html: schemaScript,
            }}
          />
        )}
      </Helmet>

      <main>
        <DynamicBanner key={location.pathname.startsWith("/it-products") ? "it-products" : "products"} banners={pageBanners} loading={bannersLoading} />

        <section
          className="
    grid
    grid-cols-1
    lg:grid-cols-[280px_1fr]
    gap-8
    px-6
    py-12
    items-start
    max-w-full
    overflow-hidden
  "
        >
          <ProductFilter
            filters={filters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            search={search}
            setSearch={setSearch}
            isTrendingFromURL={isTrendingFromURL}
            type={isIpNetworkRoute ? "ip-network" : "catalog"}
          />

          <div className="self-start">
            <div className="self-start w-full">
              {loading ? (
                <div className="min-h-[400px] flex items-center justify-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="h-12 w-12 rounded-full border-4 border-gray-200 border-t-blue-600 animate-spin" />
                    <p className="text-sm text-gray-600">Loading products...</p>
                  </div>
                </div>
              ) : products.length > 0 ? (
                <>
                  <div
                    className="
          grid
          grid-cols-1
          sm:grid-cols-2
          xl:grid-cols-4
          gap-6
        "
                  >
                    {products.map((product, index) => (
                      <ProductCard key={product._id || index} item={product} />
                    ))}
                  </div>

                  <Pagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={(newPage) => {
                      setPagination((prev) => ({ ...prev, page: newPage }));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  />
                </>
              ) : (
                <div
                  className="
        min-h-[300px]
        flex
        flex-col
        items-center
        justify-center
        rounded-lg
        border
        border-slate-200
        bg-white
        text-center
        px-5
      "
                >
                  <h2 className="text-xl font-semibold text-slate-700">
                    No Products Found
                  </h2>

                  <p className="mt-2 text-sm text-slate-500">
                    We couldn't find any products matching your selected filter.
                  </p>

                  <button
                    onClick={() =>
                      setSelectedFilters({
                        brand: "",
                        category: "",
                        subCategory: "",
                        trending: false,
                      })
                    }
                    className="
          mt-5
          px-5
          py-2
          rounded
          bg-blue-600
          text-white
        "
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ProductListing;
